import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles, Heart, Hand, MessageSquare, Mic, AudioLines, Camera, Check, Lock, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_roti-ready-ai/artifacts/emap8c6j_ChatGPT%20Image%20Apr%2024%2C%202026%2C%2010_43_07%20PM.png";
const JASMINE_IMG = "https://customer-assets.emergentagent.com/job_roti-ready-ai/artifacts/qybjphex_ChatGPT%20Image%20Apr%2024%2C%202026%20at%2005_40_27%20PM.png";
const KOKILA_IMG = "https://customer-assets.emergentagent.com/job_roti-ready-ai/artifacts/tuip86w1_ChatGPT%20Image%20Apr%2024%2C%202026%20at%2004_47_22%20PM.png";
// Warm, homely single-bowl meal photo for the final CTA — replaces the abstract ✺ glyph with a real ready-meal moment
const FINAL_CTA_MEAL_IMG = "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?auto=format&fit=crop&w=600&q=80";

export default function Landing() {
    return (
        <div className="min-h-screen bg-bone text-ink overflow-x-hidden" data-testid="landing-page">
            {/* TOP BAR — bold ink, no glass nonsense */}
            <nav className="bg-ink text-bone">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2" data-testid="brand">
                        <span className="text-saffron text-lg leading-none">✺</span>
                        <span className="font-heading text-xl font-semibold tracking-tight">My Cooking <em className="text-saffron not-italic">Assistant</em></span>
                    </div>
                    <Link to="/quiz" className="btn-saffron text-sm hidden sm:inline-flex" data-testid="nav-cta">
                        Get Started <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </nav>

            {/* HERO */}
            <section className="bg-bone relative" data-testid="hero">
                <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-12">
                    <div className="text-center mb-7 animate-fade-up">
                        <span className="tag tag-saffron" data-testid="hero-eyebrow">For busy moms · India & diaspora</span>
                        <p className="mt-4 text-sm sm:text-base text-ink/80 max-w-xl mx-auto leading-relaxed" data-testid="hero-languages-top">
                            Speaks English, <span className="font-medium">ગુજરાતી</span> &amp; <span className="font-medium">हिन्दी</span> — works the way your family actually talks at home.
                        </p>
                    </div>

                    <h1 className="sr-only">Stop wondering what to cook — your personal AI cooking assistant</h1>

                    <div className="relative mx-auto max-w-4xl animate-fade-up">
                        <div className="absolute -inset-2 sm:-inset-3 bg-saffron rotate-[-1deg] rounded-2xl" aria-hidden />
                        <div className="absolute -inset-2 sm:-inset-3 bg-teal rotate-[1deg] rounded-2xl opacity-60" aria-hidden />
                        <div className="relative rounded-2xl overflow-hidden border-2 border-ink">
                            <img src={HERO_IMG} alt="Stop wondering what to cook — from cooking stress to dinner peace" className="w-full block" data-testid="hero-image" />
                        </div>
                        <div className="absolute -top-5 -right-3 sm:-top-6 sm:-right-6 rotate-6 hidden sm:block">
                            <div className="sticker">$4.99 / ₹399</div>
                        </div>
                    </div>

                    <div className="mt-12 text-center animate-fade-up">
                        <p className="font-heading italic text-3xl sm:text-4xl lg:text-5xl text-ink max-w-2xl mx-auto leading-tight" data-testid="hero-hinglish">
                            <em className="text-saffron-dark font-medium not-italic">Aaj kya pakaye?</em> <span className="font-light">Pooch lo apni</span> Cooking Assistant <span className="font-light">se.</span>
                        </p>
                        <p className="mt-4 text-base sm:text-lg text-muted2 max-w-2xl mx-auto leading-snug">
                            Built for <em>your</em> family, your kitchen, your time — in 60 seconds.
                        </p>
                        <Link to="/quiz" className="btn-saffron text-base mt-7" data-testid="hero-cta">
                            Personalize My Assistant <ArrowRight className="w-4 h-4" />
                        </Link>
                        <p className="mt-5 text-sm text-muted2 flex items-center justify-center gap-2 flex-wrap" data-testid="hero-trust">
                            <Clock className="w-4 h-4" /> 60-second setup <span className="divider-bullet">·</span> Delivered in 24 hrs <span className="divider-bullet">·</span> One-time payment
                        </p>
                    </div>
                </div>
            </section>

            {/* MARQUEE STRIP */}
            <section className="bg-ink text-bone py-4 border-y-2 border-ink overflow-hidden" data-testid="marquee" aria-label="Cuisines we know">
                <div className="strip-marquee" tabIndex={0}>
                    <div className="strip-marquee-track">
                        {["a", "b"].map((k) => (
                            <span key={`marquee-${k}`} aria-hidden={k === "b"} className="flex items-center gap-10 font-heading text-2xl italic font-light shrink-0">
                                <span>South Indian</span><span className="text-saffron">✺</span>
                                <span>Gujarati</span><span className="text-saffron">✺</span>
                                <span>Bengali</span><span className="text-saffron">✺</span>
                                <span>Punjabi</span><span className="text-saffron">✺</span>
                                <span>Jain</span><span className="text-saffron">✺</span>
                                <span>Maharashtrian</span><span className="text-saffron">✺</span>
                                <span>Vegan</span><span className="text-saffron">✺</span>
                                <span>NRI kitchens</span><span className="text-saffron">✺</span>
                                <span>Kid-friendly</span><span className="text-saffron">✺</span>
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* SHOWCASE — built one mom at a time */}
            <section className="bg-bone py-20 sm:py-24" data-testid="showcase-section">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mb-12">
                        <span className="tag tag-teal">Built one mom at a time</span>
                        <h2 className="font-heading font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-5">
                            Every assistant is <em className="text-saffron-dark font-medium">one of one.</em>
                        </h2>
                        <p className="mt-4 text-lg text-muted2">
                            Each cooking assistant is hand-built around the family it serves. Here are two real ones we've made — including one for my own mom.
                        </p>
                    </div>

                    <div className="mb-6 flex items-center justify-center gap-3" data-testid="examples-label">
                        <span className="h-px w-10 bg-ink/40" />
                        <span className="tag">Examples · Real assistants built so far</span>
                        <span className="h-px w-10 bg-ink/40" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-3xl mx-auto">
                        <img src={JASMINE_IMG} alt="Jasmine Auntie's Sattvic Meals Guide — example assistant" className="w-full aspect-square object-cover rounded-2xl border-2 border-ink" style={{ boxShadow: '6px 6px 0 #1A1A1A' }} data-testid="showcase-jasmine" />
                        <div className="relative">
                            <img src={KOKILA_IMG} alt="Kokila Ben's Rasoi Assistant — example assistant" className="w-full aspect-square object-cover rounded-2xl border-2 border-ink" style={{ boxShadow: '6px 6px 0 #1A1A1A' }} data-testid="showcase-kokila" />
                            <div className="absolute -top-4 -right-3 sm:-top-5 sm:-right-5 -rotate-6" data-testid="thats-my-mom">
                                <div className="sticker text-base sm:text-lg flex items-center gap-1.5">
                                    That's my mom <span className="text-base">💛</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-16 text-center" data-testid="showcase-tagline">
                        <p className="font-heading italic text-4xl sm:text-5xl lg:text-6xl leading-none text-ink">
                            <span className="text-saffron-dark mr-2">✺</span>
                            Helping <em className="text-saffron-dark font-medium">one mom</em> at a time
                            <span className="text-saffron-dark ml-2">✺</span>
                        </p>
                        <p className="mt-5 flex items-center justify-center gap-2 text-base sm:text-lg text-muted2 font-heading italic" data-testid="inspired-line">
                            <span>Inspired by moms everywhere</span>
                            <Heart className="w-4 h-4 text-saffron-dark" fill="#C9711B" />
                        </p>
                        <Link to="/quiz" className="btn-saffron cta-pulse text-base mt-9 inline-flex" data-testid="showcase-cta">
                            Personalize My Assistant <ArrowRight className="w-4 h-4" />
                        </Link>
                        <p className="mt-3 text-xs uppercase tracking-[0.22em] text-muted2 font-semibold">
                            Takes 30 seconds <span className="divider-bullet">·</span> $4.99 / ₹399
                        </p>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="bg-bone py-20 sm:py-24" data-testid="how-section">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mb-12">
                        <span className="tag mb-5">How it works</span>
                        <h2 className="font-heading font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-4">
                            From <em className="font-medium">"what to cook?"</em> to <em className="text-saffron-dark font-medium">"let's eat."</em>
                        </h2>
                        <p className="mt-4 text-lg text-muted2">Three steps. No app to download. Yours forever.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { n: "01", t: "Tell us about you", d: "7 quick questions about your family, diet, time, and tastes. 60 seconds.", color: "bg-saffron" },
                            { n: "02", t: "We build your assistant", d: "Personalized to your home — local ingredients, your cuisine, your rhythm.", color: "bg-teal text-bone" },
                            { n: "03", t: "Ask it. Anytime.", d: "\"What should I cook tonight?\" Done — for the rest of your cooking life.", color: "bg-mustard" },
                        ].map((s) => (
                            <div key={s.n} className={`${s.color} rounded-2xl p-7 border-2 border-ink relative`} style={{ boxShadow: '5px 5px 0 #1A1A1A' }} data-testid={`step-${s.n}`}>
                                <p className="font-heading text-7xl font-light leading-none">{s.n}</p>
                                <h3 className={`font-heading text-2xl mt-3 mb-2 leading-snug ${s.color.includes('text-bone') ? '' : 'text-ink'}`}>{s.t}</h3>
                                <p className={`text-base leading-relaxed ${s.color.includes('text-bone') ? 'text-bone/85' : 'text-ink/80'}`}>{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FIVE WAYS — make it dead-simple for moms */}
            <section className="bg-cream py-20 sm:py-24 border-t-2 border-ink" data-testid="five-ways-section">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl mx-auto text-center mb-12">
                        <span className="tag tag-saffron">5 simple ways to use it</span>
                        <h2 className="font-heading font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-5">
                            Whatever's <em className="text-saffron-dark font-medium">easiest in the moment.</em>
                        </h2>
                        <p className="mt-4 text-lg text-muted2">
                            Hands full? Just speak. Curious about the fridge? Just snap. No app to learn.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="five-ways-grid">
                        {[
                            {
                                n: "01",
                                icon: <Hand className="w-6 h-6" strokeWidth={2} />,
                                t: "Tap a suggestion",
                                d: "Pre-built buttons like \"Tonight's dinner\" or \"Quick 15-min meal\". One tap. Done.",
                                bg: "bg-saffron",
                            },
                            {
                                n: "02",
                                icon: <MessageSquare className="w-6 h-6" strokeWidth={2} />,
                                t: "Type a craving",
                                d: "\"Something light tonight?\" or \"Kids want pasta\" — just type and it figures it out.",
                                bg: "bg-bone",
                            },
                            {
                                n: "03",
                                icon: <Mic className="w-6 h-6" strokeWidth={2} />,
                                t: "Speak your idea",
                                d: "Hit the mic. Say it in English, ગુજરાતી or हिन्दी. It listens and replies.",
                                bg: "bg-mustard",
                            },
                            {
                                n: "04",
                                icon: <AudioLines className="w-6 h-6" strokeWidth={2} />,
                                t: "Hands-free chat",
                                d: "Tap the waveform — full voice conversation while your hands are in the dough.",
                                bg: "bg-teal text-bone",
                            },
                            {
                                n: "05",
                                icon: <Camera className="w-6 h-6" strokeWidth={2} />,
                                t: "Snap your fridge",
                                d: "Take a photo of what you have and it tells you what you can cook tonight.",
                                bg: "bg-saffron",
                            },
                        ].map((w) => {
                            const dark = w.bg.includes("text-bone");
                            return (
                                <div
                                    key={w.n}
                                    className={`${w.bg} rounded-2xl p-6 border-2 border-ink relative`}
                                    style={{ boxShadow: '5px 5px 0 #1A1A1A' }}
                                    data-testid={`way-${w.n}`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`w-11 h-11 rounded-full border-2 border-ink flex items-center justify-center ${dark ? 'bg-bone text-ink' : 'bg-ink text-bone'}`}>
                                            {w.icon}
                                        </span>
                                        <span className={`font-heading text-2xl font-light ${dark ? 'text-bone/60' : 'text-ink/40'}`}>{w.n}</span>
                                    </div>
                                    <h3 className={`font-heading text-xl leading-snug ${dark ? 'text-bone' : 'text-ink'}`}>{w.t}</h3>
                                    <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-bone/85' : 'text-ink/75'}`}>{w.d}</p>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-10 text-center text-base text-muted2 italic">
                        No typing required if you don't want to.
                    </p>
                </div>
            </section>

            {/* MENU CARD — what you get */}
            <section className="bg-cream py-20 sm:py-24 border-y-2 border-ink" data-testid="value-section">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <div className="text-center mb-10">
                        <span className="tag tag-teal">On the menu</span>
                        <h2 className="font-heading font-light text-4xl sm:text-5xl leading-tight mt-5">
                            A kitchen helper that <em className="text-saffron-dark font-medium">actually knows you.</em>
                        </h2>
                    </div>

                    <div className="bg-bone border-2 border-ink rounded-2xl p-6 sm:p-10" style={{ boxShadow: '8px 8px 0 #1A1A1A' }}>
                        <div className="text-center mb-7 pb-6 border-b-2 border-dashed border-ink/20">
                            <p className="font-heading italic text-2xl text-muted2">Today's specials</p>
                            <div className="flex justify-center items-center gap-2 mt-2 text-saffron text-lg">✺ ◆ ✺</div>
                        </div>
                        <ul className="space-y-5">
                            {[
                                { t: "Built around your diet", d: "Jain, vegan, or vegetarian — set it once, never explain again." },
                                { t: "Knows your time", d: "10-min weeknight saviors, 40-min weekend feasts. Always realistic." },
                                { t: "Fits your household", d: "Cooking for kids, elderly, just you, or a joint family of 12 — it adapts." },
                                { t: "Remembers what you avoid", d: "No onion-garlic? Allergic to mushrooms? Your assistant never forgets." },
                                { t: "Local to your kitchen", d: "Suggests what's seasonal in your city — not a generic global recipe DB." },
                                { t: "Yours forever", d: "One-time payment. No subscription. No app. No expiry." },
                            ].map((b, i) => (
                                <li key={b.t} className="flex items-start gap-4">
                                    <span className="font-heading font-medium text-saffron-dark text-lg leading-tight shrink-0 w-7">{String(i + 1).padStart(2, "0")}</span>
                                    <div className="flex-1">
                                        <p className="font-heading text-xl text-ink leading-snug">{b.t}</p>
                                        <p className="text-base text-muted2 mt-0.5 leading-relaxed">{b.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* FINAL CTA — DECISION MOMENT BLOCK */}
            <section className="bg-ink text-bone py-20 sm:py-28 text-center relative overflow-hidden" data-testid="final-cta-section">
                {/* soft saffron radial wash for warmth */}
                <div className="absolute inset-0 pointer-events-none opacity-25" style={{ background: 'radial-gradient(ellipse at center, rgba(229,138,45,0.35) 0%, transparent 60%)' }} aria-hidden />

                <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
                    <div className="flex justify-center mb-8">
                        <img
                            src={FINAL_CTA_MEAL_IMG}
                            alt="A homely Indian meal — ready to eat"
                            className="w-44 h-44 sm:w-52 sm:h-52 object-cover rounded-full border-4 border-saffron"
                            style={{ boxShadow: '6px 6px 0 #E58A2D' }}
                            data-testid="final-cta-meal"
                        />
                    </div>

                    <h2 className="font-heading font-light text-4xl sm:text-6xl leading-[1.05]">
                        Tonight's dinner — <em className="text-saffron font-medium">already decided.</em>
                    </h2>

                    {/* 1. Micro-context */}
                    <p className="mt-6 text-base sm:text-lg text-bone/65 max-w-xl mx-auto" data-testid="cta-micro-context">
                        Start using your assistant as early as <strong className="text-bone/90 font-medium">tomorrow</strong>.
                    </p>

                    {/* 2. Arrow + microcopy guide */}
                    <div className="mt-9 flex flex-col items-center gap-2 text-saffron" data-testid="cta-guide" aria-hidden="true">
                        <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-saffron/85">Takes 30 seconds →</span>
                        <svg className="cta-bounce w-7 h-7 sm:w-8 sm:h-8" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 4 C 16 18, 16 22, 16 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                            <path d="M9 25 L16 33 L23 25" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    {/* 3. Primary CTA — single action, quiz-first */}
                    <div className="mt-3 flex flex-col items-stretch gap-2 max-w-sm mx-auto" data-testid="cta-stack">
                        <Link
                            to="/quiz"
                            className="btn-cta-primary cta-pulse w-full"
                            data-testid="final-cta"
                        >
                            <span>Customize my assistant</span>
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <p className="mt-1 text-center text-xs sm:text-sm text-bone/55" data-testid="cta-microcopy">
                            Takes 30 seconds <span className="text-saffron/70">•</span> One-time payment
                        </p>
                    </div>

                    {/* 5. Trust bar — kept lean, no overlap with microcopy */}
                    <ul className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-[13px] text-bone/65" data-testid="trust-bar">
                        <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-saffron" /> No subscription</li>
                        <li className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-saffron" /> Secure checkout</li>
                        <li className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-saffron" /> Built within 24 hrs</li>
                    </ul>

                    {/* 6. Closing line */}
                    <p className="mt-12 font-heading italic text-2xl sm:text-3xl text-bone/85 leading-snug max-w-xl mx-auto" data-testid="cta-closing">
                        This isn't a recipe app.
                        <br />It's <em className="text-saffron font-medium">a decision made for you.</em>
                    </p>

                    <p className="mt-10 text-base sm:text-lg text-bone/55 flex items-center justify-center gap-2 font-heading italic" data-testid="final-inspired">
                        <span>Inspired by moms everywhere</span>
                        <Heart className="w-4 h-4 text-saffron" fill="#E58A2D" />
                    </p>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-bone border-t-2 border-ink py-8">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-saffron">✺</span>
                        <span className="font-heading text-lg">My Cooking <em className="text-saffron-dark not-italic">Assistant</em></span>
                    </div>
                    <p className="text-xs text-muted2">© 2026 · Made with love for home kitchens</p>
                </div>
            </footer>
        </div>
    );
}
